import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { toast } from "react-toastify";
import {
	Loader2,
	AlertTriangle,
	Sparkles,
	MessageCircle,
	Info,
	Wallet,
	ChevronDown,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
	Select,
	SelectTrigger,
	SelectValue,
	SelectContent,
	SelectItem,
} from "../../components/ui/select";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const ProspeccaoConfig = () => {
	const [ajustes, setAjustes] = useState(null);
	const [fontes, setFontes] = useState([]);
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);
	// A chave do Google volta mascarada do servidor, então o campo começa
	// vazio: o que está gravado aparece no placeholder e só é substituído se
	// alguém digitar uma nova.
	const [chaveGoogle, setChaveGoogle] = useState("");
	const [removerChaveGoogle, setRemoverChaveGoogle] = useState(false);

	const carregar = useCallback(async () => {
		setCarregando(true);
		try {
			const [resAjustes, resFontes] = await Promise.all([
				api.get("/prospect/settings"),
				api.get("/prospect/sources"),
			]);
			setAjustes(resAjustes.data);
			setFontes(Array.isArray(resFontes.data) ? resFontes.data : []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, []);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const alterar = (campo, valor) =>
		setAjustes((atual) => ({ ...atual, [campo]: valor }));

	// `fontesHabilitadas` vazio significa "todas". A tela mostra um interruptor
	// por fonte e guarda a lista só quando alguma está desligada — assim o
	// padrão (tudo ligado) continua sendo o vazio e uma fonte nova registrada
	// no backend já nasce habilitada, sem precisar mexer em nada aqui.
	const fonteLigada = (key) => {
		const lista = ajustes?.fontesHabilitadas || [];
		return lista.length === 0 || lista.includes(key);
	};

	const alternarFonte = (key, ligada) => {
		const todas = fontes.map((f) => f.key);
		const ligadas = ajustes.fontesHabilitadas?.length
			? [...ajustes.fontesHabilitadas]
			: [...todas];
		const proxima = ligada
			? Array.from(new Set([...ligadas, key]))
			: ligadas.filter((k) => k !== key);
		// Tudo ligado volta a ser a lista vazia, que é o significado de "todas".
		const normalizada = todas.every((k) => proxima.includes(k)) ? [] : proxima;
		alterar("fontesHabilitadas", normalizada);
	};

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			// A chave mascarada nunca volta para o servidor: o campo carrega
			// apenas o texto novo, e vazio significa "manter a que já está".
			const payload = { ...ajustes };
			delete payload.googlePlacesApiKey;

			payload.limiteResultadosPorPesquisa = Number(
				ajustes.limiteResultadosPorPesquisa
			);
			payload.limitePesquisasPorDia = Number(ajustes.limitePesquisasPorDia);
			payload.retencaoDias = Number(ajustes.retencaoDias);

			if (chaveGoogle.trim()) {
				payload.googlePlacesApiKey = chaveGoogle.trim();
			} else if (removerChaveGoogle) {
				payload.googlePlacesApiKey = "";
			}

			const { data } = await api.put("/prospect/settings", payload);

			setAjustes(data);
			setChaveGoogle("");
			setRemoverChaveGoogle(false);
			toast.success("Ajustes salvos. Valem para as próximas pesquisas.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	if (carregando || !ajustes) {
		return (
			<MainContainer>
				<MainHeader>
					<Title>Config. de prospecção</Title>
				</MainHeader>
				<div className="flex-1 px-4 pb-4 sm:px-6 sm:pb-6">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			</MainContainer>
		);
	}

	return (
		<MainContainer>
			<MainHeader>
				<Title>Config. de prospecção</Title>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Os limites e o que a prospecção usa por padrão nesta empresa. Estes
					valores valem para as próximas pesquisas; uma pesquisa que já está
					rodando termina com o que valia quando começou.
				</p>

				<form onSubmit={salvar} className="max-w-3xl space-y-5">
					<div className="space-y-5 rounded-xl border bg-card p-5">
						<div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
							<div>
								<Label htmlFor="limiteResultadosPorPesquisa">
									Limite de resultados por pesquisa
								</Label>
								<Input
									id="limiteResultadosPorPesquisa"
									type="number"
									min="1"
									max="1000"
									className="mt-1"
									value={ajustes.limiteResultadosPorPesquisa}
									onChange={(e) =>
										alterar("limiteResultadosPorPesquisa", e.target.value)
									}
								/>
								<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
									Nenhuma pesquisa passa deste número, mesmo que quem a criou
									peça mais. Entre 1 e 1000.
								</p>
							</div>

							<div>
								<Label htmlFor="limitePesquisasPorDia">
									Pesquisas por dia
								</Label>
								<Input
									id="limitePesquisasPorDia"
									type="number"
									min="0"
									max="1000"
									className="mt-1"
									value={ajustes.limitePesquisasPorDia}
									onChange={(e) =>
										alterar("limitePesquisasPorDia", e.target.value)
									}
								/>
								<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
									Zero desliga o limite. Cada pesquisa consulta um serviço
									externo, então um teto evita que a conta seja bloqueada por
									excesso de uso.
								</p>
							</div>
						</div>

						<div className="border-t pt-4">
							<Label htmlFor="retencaoDias">Retenção dos resultados</Label>
							<div className="mt-1 flex items-center gap-2">
								<Input
									id="retencaoDias"
									type="number"
									min="0"
									className="w-28"
									value={ajustes.retencaoDias}
									onChange={(e) => alterar("retencaoDias", e.target.value)}
								/>
								<span className="text-sm text-muted-foreground">dias</span>
							</div>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Zero significa sem expiração. Definir um prazo ajuda a cumprir
								política de retenção de dados — dados de empresas que não
								viraram contato não ficam guardados para sempre.
							</p>
						</div>

						<div className="border-t pt-4">
							<Label>Preferências de localização</Label>
							<div className="mt-1 grid grid-cols-1 gap-3 sm:grid-cols-3">
								<div>
									<span className="text-xs text-muted-foreground">País</span>
									<Input
										className="mt-1"
										value={ajustes.paisPadrao}
										onChange={(e) => alterar("paisPadrao", e.target.value)}
									/>
								</div>
								<div>
									<span className="text-xs text-muted-foreground">Estado</span>
									<Input
										className="mt-1"
										value={ajustes.estadoPadrao}
										onChange={(e) => alterar("estadoPadrao", e.target.value)}
									/>
								</div>
								<div>
									<span className="text-xs text-muted-foreground">Cidade</span>
									<Input
										className="mt-1"
										value={ajustes.cidadePadrao}
										onChange={(e) => alterar("cidadePadrao", e.target.value)}
									/>
								</div>
							</div>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Vem preenchido no formulário de uma pesquisa nova. Quem cria a
								pesquisa ainda pode mudar.
							</p>
						</div>

						<div className="border-t pt-4">
							<Label htmlFor="tratamentoDuplicados">
								Quando o lead já existe no CRM
							</Label>
							<Select
								value={ajustes.tratamentoDuplicados}
								onValueChange={(v) => alterar("tratamentoDuplicados", v)}
							>
								<SelectTrigger id="tratamentoDuplicados" className="mt-1 w-full sm:w-80">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="ignorar">
										Ignorar o que já está no CRM
									</SelectItem>
									<SelectItem value="adicionar">
										Adicionar mesmo assim
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Vale na importação para uma lista de contatos. Nunca
								sobrescreve o cadastro existente.
							</p>
						</div>

						<div className="border-t pt-4">
							<Label htmlFor="overpassUrl">
								Endereço do serviço do OpenStreetMap
							</Label>
							<Input
								id="overpassUrl"
								className="mt-1 font-mono text-xs"
								value={ajustes.overpassUrl}
								onChange={(e) => alterar("overpassUrl", e.target.value)}
							/>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Usado quando não há chave do Google Places. Deixe como está
								para usar o serviço público do OpenStreetMap; só mude se a rede
								desta instalação exigir um intermediário próprio.
							</p>
						</div>
					</div>

					{/* Fontes de pesquisa: o que existe de fato neste ambiente. */}
					<div className="space-y-4 rounded-xl border bg-card p-5">
						<div>
							<Label>Fontes de pesquisa</Label>
							<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
								Desligar uma fonte impede que ela seja usada nas próximas
								pesquisas. Uma fonte indisponível continua listada — escondê-la
								daria a impressão de que não existe.
							</p>
						</div>

						{fontes.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								Nenhuma fonte registrada.
							</p>
						) : (
							<div className="space-y-3">
								{fontes.map((fonte) => (
									<div
										key={fonte.key}
										className="flex items-start gap-3 rounded-lg border p-3"
									>
										<Checkbox
											id={`fonte-${fonte.key}`}
											className="mt-0.5"
											checked={fonteLigada(fonte.key)}
											disabled={!fonte.disponivel}
											onCheckedChange={(v) =>
												alternarFonte(fonte.key, Boolean(v))
											}
										/>
										<div className="min-w-0 flex-1">
											<div className="flex flex-wrap items-center gap-2">
												<label
													htmlFor={`fonte-${fonte.key}`}
													className="cursor-pointer text-sm font-medium"
												>
													{fonte.nome}
												</label>
												{fonte.disponivel ? (
													<Badge variant="default">Disponível</Badge>
												) : (
													<Badge variant="secondary">Indisponível</Badge>
												)}
												{fonte.requerCredencial && (
													<Badge variant="outline">Exige credencial</Badge>
												)}
											</div>
											<p className="mt-1 text-xs leading-relaxed text-muted-foreground">
												{fonte.descricao}
											</p>
											{fonte.motivo && (
												<p className="mt-1.5 flex gap-1.5 rounded-md bg-muted p-2 text-xs leading-relaxed">
													<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
													{fonte.motivo}
												</p>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* A chave fica fora do fluxo genérico de ajustes: ela não volta
					    do servidor por inteiro, então só é gravada quando alguém
					    digita uma nova. */}
					<div className="space-y-4 rounded-xl border bg-card p-5">
						<div>
							<Label htmlFor="googlePlacesApiKey">
								Chave da API do Google Places
							</Label>
							<p className="text-sm leading-relaxed text-muted-foreground">
								O Google Places é a fonte padrão da prospecção. Sem chave, a
								pesquisa usa o OpenStreetMap — que é público e não exige
								credencial nem cobrança.
							</p>
						</div>

						<div className="flex items-center gap-2">
							<Input
								id="googlePlacesApiKey"
								type="password"
								autoComplete="off"
								className="font-mono text-xs"
								placeholder={
									ajustes.googlePlacesApiKey || "Não configurada"
								}
								value={chaveGoogle}
								onChange={(e) => {
									setChaveGoogle(e.target.value);
									if (e.target.value) setRemoverChaveGoogle(false);
								}}
							/>
							{ajustes.googlePlacesApiKey && !removerChaveGoogle && (
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => {
										setChaveGoogle("");
										setRemoverChaveGoogle(true);
									}}
								>
									Remover
								</Button>
							)}
						</div>

						{ajustes.googlePlacesApiKey && !removerChaveGoogle && (
							<p className="text-xs leading-relaxed text-muted-foreground">
								Chave gravada:{" "}
								<span className="font-mono">
									{ajustes.googlePlacesApiKey}
								</span>
								. Deixe o campo vazio para mantê-la ou digite outra para
								substituir.
							</p>
						)}

						{removerChaveGoogle && (
							<p className="text-xs font-medium text-destructive">
								A chave será removida ao salvar. A prospecção volta a usar o
								OpenStreetMap.
							</p>
						)}

						{/* Custo: a franquia do Google não é óbvia e o faturamento
						    pode pegar a empresa de surpresa, então vem antes do
						    tutorial. */}
						<div className="flex gap-2 rounded-lg border bg-muted/40 p-3">
							<Wallet className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
							<div className="space-y-1.5 text-xs leading-relaxed">
								<p className="font-medium text-foreground">
									Tem custo — só depois da franquia gratuita
								</p>
								<ul className="list-disc space-y-1 pl-4 text-muted-foreground">
									<li>
										<strong className="font-medium text-foreground">
											1.000 pesquisas grátis por mês
										</strong>{" "}
										(Text Search, a faixa que devolve telefone e site).
									</li>
									<li>
										Depois disso,{" "}
										<strong className="font-medium text-foreground">
											US$ 35,00
										</strong>{" "}
										a cada 1.000 pesquisas — cerca de US$ 0,035 por pesquisa.
									</li>
									<li>
										Cada página de resultado (até 20 empresas) conta como uma
										pesquisa, e o sistema pede uma página a cada 20 resultados:
										uma pesquisa de 50 resultados consome 3.
									</li>
									<li>
										A cobrança é do Google, em dólar, na conta de faturamento
										da empresa. O Perabit não intermedia nem repassa esse valor.
									</li>
									<li>
										Sem chave configurada não há cobrança nenhuma: a pesquisa
										segue pelo OpenStreetMap.
									</li>
								</ul>
								<p>
									<a
										href="https://developers.google.com/maps/billing-and-pricing/pricing"
										target="_blank"
										rel="noreferrer"
										className="underline underline-offset-2 hover:text-foreground"
									>
										Ver a tabela de preços oficial do Google
									</a>
									.
								</p>
							</div>
						</div>

						<details
							open={!ajustes.googlePlacesApiKey}
							className="group rounded-lg border"
						>
							<summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm font-medium [&::-webkit-details-marker]:hidden">
								<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
								Onde pegar essa chave, passo a passo
							</summary>
							<div className="space-y-3 border-t px-3 py-3 text-xs leading-relaxed text-muted-foreground">
								<ol className="list-decimal space-y-2 pl-4">
									<li>
										Entre no{" "}
										<a
											href="https://console.cloud.google.com/"
											target="_blank"
											rel="noreferrer"
											className="underline underline-offset-2 hover:text-foreground"
										>
											Google Cloud Console
										</a>{" "}
										com a conta Google da empresa e aceite os termos do Google
										Maps Platform, que aparecem no primeiro acesso.
									</li>
									<li>
										Crie um projeto (ou use um que já exista) e, em{" "}
										<a
											href="https://console.cloud.google.com/billing"
											target="_blank"
											rel="noreferrer"
											className="underline underline-offset-2 hover:text-foreground"
										>
											Faturamento
										</a>
										, vincule uma conta de faturamento com cartão. O Google
										exige isso mesmo para usar só a parte gratuita.
									</li>
									<li>
										Em{" "}
										<a
											href="https://console.cloud.google.com/apis/library/places.googleapis.com"
											target="_blank"
											rel="noreferrer"
											className="underline underline-offset-2 hover:text-foreground"
										>
											APIs e serviços › Biblioteca
										</a>
										, procure por <strong>Places API (New)</strong> e clique em
										Ativar.
									</li>
									<li>
										Em{" "}
										<a
											href="https://console.cloud.google.com/apis/credentials"
											target="_blank"
											rel="noreferrer"
											className="underline underline-offset-2 hover:text-foreground"
										>
											APIs e serviços › Credenciais
										</a>
										, clique em <strong>Criar credenciais › Chave de API</strong>{" "}
										e copie o valor gerado.
									</li>
									<li>
										Recomendado: clique na chave criada e restrinja. Em{" "}
										<em>Restrições de API</em>, marque apenas{" "}
										<strong>Places API (New)</strong>; em{" "}
										<em>Restrições de aplicativo</em>, escolha{" "}
										<strong>Endereços IP</strong> e informe o IP do servidor.
										Assim, se a chave vazar, ela não serve em outro lugar.
									</li>
									<li>
										Recomendado: em Faturamento, crie um{" "}
										<strong>alerta de orçamento</strong> para ser avisado antes
										de qualquer cobrança relevante.
									</li>
									<li>
										Cole a chave no campo acima e clique em{" "}
										<strong>Salvar ajustes</strong>. Ela fica guardada no
										servidor e não volta mais por inteiro para a tela.
									</li>
								</ol>
							</div>
						</details>
					</div>

					{/* IA e WhatsApp: nada é reconfigurado aqui — as telas do
					    sistema continuam sendo o lugar único de cada assunto. */}
					<div className="space-y-3 rounded-xl border bg-card p-5">
						<Label>Inteligência artificial e WhatsApp</Label>

						<div className="flex items-start gap-3 rounded-lg border p-3">
							<Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
							<div className="flex-1">
								<p className="text-sm">
									A IA da prospecção usa a integração já configurada no
									sistema — provedor, modelo e chave continuam em{" "}
									<Link
										to="/ai"
										className="underline underline-offset-2 hover:text-foreground"
									>
										Talk.Ai
									</Link>
									. Nada é duplicado aqui.
								</p>
							</div>
						</div>

						<div className="flex items-start gap-3 rounded-lg border p-3">
							<MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
							<div className="flex-1">
								<p className="text-sm">
									A conferência de WhatsApp usa as conexões existentes em{" "}
									<Link
										to="/connections"
										className="underline underline-offset-2 hover:text-foreground"
									>
										Conexões
									</Link>
									. É preciso ter uma conexão WhatsApp (não oficial)
									conectada; sem ela, a conferência fica indisponível e os
									leads permanecem como não verificados.
								</p>
							</div>
						</div>
					</div>

					<p className="flex gap-1.5 rounded-md bg-muted p-3 text-xs leading-relaxed">
						<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						Alterar estes valores fica registrado na auditoria do sistema.
					</p>

					<Button type="submit" disabled={salvando}>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						Salvar ajustes
					</Button>
				</form>
			</div>
		</MainContainer>
	);
};

export default ProspeccaoConfig;
